import { BreakpointObserver } from '@angular/cdk/layout';
import { computed, inject, Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { map } from 'rxjs';

const MOBILE_BREAKPOINT = '(max-width: 767.98px)';
const TABLET_BREAKPOINT = '(min-width: 768px) and (max-width: 1799.98px)';
const DESKTOP_BREAKPOINT = '(min-width: 1800px)';

// Mobile: 0-32px
const MIN_MARGIN_MOBILE = 0;
const MAX_MARGIN_MOBILE = 32;

// Tablet: 24-72px
const MIN_MARGIN_TABLET = 24;
const MAX_MARGIN_TABLET = 72;

// Desktop: 24-120px
const MIN_MARGIN_DESKTOP = 24;
const MAX_MARGIN_DESKTOP = 120;

@Injectable({
  providedIn: 'root',
})
export class ViewportService {
  private readonly breakpointObserver = inject(BreakpointObserver);

  readonly isMobile = toSignal(
    this.breakpointObserver.observe(MOBILE_BREAKPOINT).pipe(map((result) => result.matches)),
    { initialValue: false },
  );

  readonly isTablet = toSignal(
    this.breakpointObserver.observe(TABLET_BREAKPOINT).pipe(map((result) => result.matches)),
    { initialValue: false },
  );

  readonly isDesktop = toSignal(
    this.breakpointObserver.observe(DESKTOP_BREAKPOINT).pipe(map((result) => result.matches)),
    { initialValue: false },
  );

  readonly minMargin = computed(() => {
    if (this.isMobile()) return MIN_MARGIN_MOBILE;
    if (this.isTablet()) return MIN_MARGIN_TABLET;
    return MIN_MARGIN_DESKTOP;
  });

  readonly maxMargin = computed(() => {
    if (this.isMobile()) return MAX_MARGIN_MOBILE;
    if (this.isTablet()) return MAX_MARGIN_TABLET;
    return MAX_MARGIN_DESKTOP;
  });

  /**
   * Calculates the real margin value from a percentage (0-1)
   * The calculation is viewport-responsive:
   * - Mobile (≥320px): 0-32px
   * - Tablet (≥768px): 24-72px
   * - Desktop (≥1800px): 24-120px
   * @param percentage - Value between 0 and 1 representing the margin percentage
   * @returns The calculated margin value in pixels
   */
  calculateMarginFromPercentage(percentage: number): number {
    const min = this.minMargin();
    const max = this.maxMargin();
    const margin = min + percentage * (max - min);
    return margin;
  }

  /**
   * Calculates the percentage (0-1) from a real margin value in pixels
   * The calculation is viewport-responsive based on current breakpoint ranges
   * @param margin - The margin value in pixels
   * @returns Value between 0 and 1 representing the margin percentage
   */
  calculatePercentageFromMargin(margin: number): number {
    const min = this.minMargin();
    const max = this.maxMargin();
    const percentage = (margin - min) / (max - min);
    return percentage;
  }
}
